import dayjs from 'dayjs'
import type { BondsDetailsIssuerFactsData, BondsListingDetail } from '@/proto/bonds'
import { getDisplayRatingTags } from '@/utils/common'
import { filters } from '@/utils/filters'
import { bondV2Data } from './bondV2Data'
import type { BondV2Data } from './bondV2Context'
import {
    mapIssuerFactBasicDetails,
    mapIssuerFactCreditRatingIntelligence,
    mapIssuerFactFinancialTrend,
    mapIssuerFactIssuerMetrics,
    mapIssuerFactRegulatoryAssurance,
    mapIssuerFactStrengthsRisks
} from './bondV2IssuerFactsMapper'

const toNumber = (value: unknown): number | null => {
    if (value == null || value === '') return null
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (typeof value === 'string') {
        const parsed = Number(value.replace(/,/g, '').replace(/[^\d.-]/g, ''))
        return Number.isFinite(parsed) ? parsed : null
    }

    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : null
}

const formatAmount = (value?: unknown) => {
    const numericValue = toNumber(value)
    if (numericValue == null) return null
    return `₹${filters.formatAmount(numericValue)}`
}

const displayValue = (value?: string | null) => value || 'N/A'

const formatPercent = (value?: unknown) => {
    const numericValue = toNumber(value)
    if (numericValue == null) return null
    return `${numericValue.toFixed(2)}%`
}

const formatPositivePercent = (value?: unknown) => {
    const numericValue = getPositiveAmount(value)
    if (numericValue == null) return null
    return `${numericValue.toFixed(2)}%`
}

const formatPriceNumber = (value: unknown) => {
    const numericValue = toNumber(value)
    if (numericValue == null) return 'N/A'

    return numericValue.toLocaleString('en-IN', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4
    })
}

const formatDate = (value?: string | null, fallback = 'N/A') => {
    if (!value) return fallback
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('DD MMM YYYY') : value
}

const formatPayout = (frequency?: string | null) => {
    if (!frequency) return null

    const payoutMap: Record<string, string> = {
        Monthly: 'Monthly',
        Quarterly: 'Quarterly',
        'Half Yearly': 'Half Yearly',
        Yearly: 'Yearly',
        'On Maturity': 'At Maturity',
        'On Redemption': 'At Maturity'
    }

    return payoutMap[frequency] ?? frequency
}

const compact = <T>(items: Array<T | null | undefined | false>) => {
    return items.filter(Boolean) as T[]
}

const unique = (items: string[]) => {
    return Array.from(new Set(items.map(item => item.trim()).filter(Boolean)))
}

const displayRating = (rating?: string | null) => {
    const tags = getDisplayRatingTags(rating ?? '')
    return tags[0] ?? rating ?? null
}

const displayInstrumentType = (listing: BondsListingDetail) => {
    const instrument = listing.instrument
    return instrument?.category || instrument?.issuanceType || null
}

const displaySecurity = (seniority?: string | null, issuanceType?: string | null) => {
    const type = (issuanceType || "").trim().toLowerCase()
    const sen = (seniority || "").trim().toLowerCase()

    if (!type && !sen) {
        return null
    }

    if (type === "db" && sen.includes("senior")) {
        return "Senior Debenture"
    }

    if (type === "unsecured" && (sen.includes("tier 2") || sen.includes("tier-2"))) {
        return "Tier 2 Sub Unsecured"
    }

    if (type === "unsecured" && sen === "subordinated") {
        return "Sub Unsecured"
    }

    if (type === "unsecured" && sen === "senior") {
        return "Senior Unsecured"
    }

    if (type === "secured" && sen === "senior") {
        return "Senior Secured"
    }

    if (type === "secured" && !sen) {
        return "Secured Bond"
    }

    if ((type === "unsecured" && !sen) || (!type && sen === "unsecured")) {
        return "Unsecured Bond"
    }

    // Default Fallback
    const isSecured = type === "secured" || type === "db" || sen.includes("secured")
    const isSenior = sen.includes("senior") || sen.includes("secured")

    if (isSenior || isSecured) {
        return seniority || (type === "secured" ? "Secured Bond" : "Secured")
    }

    return seniority || type || "Unsecured Bond"
}

const displayFundamentalsSecurity = (listing: BondsListingDetail) => {
    return [
        listing.instrument?.seniorityRepayment,
        listing.instrument?.issuanceType
    ].filter(Boolean).join(' ')
}

const getLotSize = (listing: BondsListingDetail) => {
    const size = Math.floor(Number(listing.lotSize) || 1)
    return Math.max(1, size)
}

const getSelectedUnits = (listing: BondsListingDetail) => {
    const minUnits = toNumber(listing.instrument?.minUnitsToSell)
    if (minUnits && minUnits > 0) {
        const lotSize = getLotSize(listing)
        return Math.ceil(minUnits / lotSize) * lotSize
    }

    const minInvestment = toNumber(listing.minInvestment)
    if (minInvestment && minInvestment > 0 && minInvestment < 1000) {
        return minInvestment
    }

    return 1
}

const getPositiveAmount = (value?: unknown) => {
    const numericValue = toNumber(value)
    if (numericValue == null || numericValue <= 0) return null
    return numericValue
}

const getPricePerUnit = (listing: BondsListingDetail) => {
    return getPositiveAmount(listing.cashflow?.totalPurchaseValueForYieldOffered)
        ?? getPositiveAmount(listing.totalPurchaseValueForYieldOffered)
}

const formatPricePair = (listing: BondsListingDetail) => {
    const cleanPrice = getPositiveAmount(listing.cashflow?.currentCleanPriceForYieldOffered)
    const dirtyPrice = getPositiveAmount(listing.cashflow?.dirtyPrice)

    if (cleanPrice == null || dirtyPrice == null) return 'N/A'
    return `${formatPriceNumber(cleanPrice)} / ${formatPriceNumber(dirtyPrice)}`
}

const formatYtmCouponSpread = (ytm?: unknown, coupon?: unknown) => {
    const ytmValue = toNumber(ytm)
    const couponValue = toNumber(coupon)
    const ytmPercent = formatPercent(ytm)
    if (!ytmPercent) return null
    if (ytmValue == null) return null
    if (couponValue == null) return ytmPercent

    const bps = Math.round((ytmValue - couponValue) * 100)
    const prefix = bps > 0 ? '+' : ''

    return `${ytmPercent} / ${prefix}${bps} bps`
}

const getCashflowsForDisplay = (listing: BondsListingDetail) => {
    return listing.cashflow?.cashflows ?? []
}

const getCashflowTabs = (listing: BondsListingDetail) => {
    const instrument = listing.instrument
    const cashflows = getCashflowsForDisplay(listing)
    const interestFrequency = formatPayout(instrument?.interestPaymentFrequency ?? instrument?.couponFrequency)
    const principalRows = cashflows
        .map((cashflow, index) => ({ cashflow, index }))
        .filter(({ cashflow }) => (toNumber(cashflow.principalAmount) || 0) > 0)

    let principal = 'Principal Schedule Unavailable'

    if (principalRows.length === 1) {
        const principalRow = principalRows[0]
        const principalDate = dayjs(principalRow.cashflow.cashflowDate)
        const maturityDate = dayjs(instrument?.maturityDate)
        const isLastCashflow = principalRow.index === cashflows.length - 1
        const isMaturityDate = principalDate.isValid() && maturityDate.isValid() && principalDate.isSame(maturityDate, 'day')

        principal = isLastCashflow || isMaturityDate
            ? 'Principal Paid at Maturity'
            : `Principal Paid on ${formatDate(principalRow.cashflow.cashflowDate)}`
    } else if (principalRows.length > 1) {
        principal = 'Principal Paid Across Schedule'
    }

    return {
        interest: interestFrequency ? `${interestFrequency} Interest Payout` : 'Interest Payout',
        principal
    }
}

const mapCashflows = (listing: BondsListingDetail, units: number) => {
    const cashflows = getCashflowsForDisplay(listing)

    return cashflows.map((cashflow) => ({
        date: formatDate(cashflow.cashflowDate, cashflow.cashflowDate),
        interest: formatAmount((toNumber(cashflow.interestAmount) || 0) * units) ?? '₹0',
        principal: toNumber(cashflow.principalAmount) ? formatAmount((toNumber(cashflow.principalAmount) || 0) * units) ?? '₹0' : '--'
    }))
}

type BondV2MapperOptions = {
    useLegacyIssuerAboutFallback?: boolean
}

type IssuerBasicDetails = ReturnType<typeof mapIssuerFactBasicDetails>
type FundamentalRow = typeof bondV2Data.fundamentals.rows[number]

const formatRatingLabel = (rating?: string | null, agency?: string | null) => {
    if (!rating) return null
    const agencySuffix = agency ? ` | ${agency}` : ''
    return `${rating}${agencySuffix}`
}

const getInvestmentSnapshot = (listing: BondsListingDetail, selectedUnits: number) => {
    const pricePerUnit = getPricePerUnit(listing)
    const investmentAmount = pricePerUnit != null
        ? pricePerUnit * selectedUnits
        : null
    const cashflowsForDisplay = getCashflowsForDisplay(listing)
    const totalCashflowInterest = cashflowsForDisplay.reduce((total, cashflow) => total + (toNumber(cashflow.interestAmount) || 0), 0) * selectedUnits
    const totalCashflowPrincipal = cashflowsForDisplay.reduce((total, cashflow) => total + (toNumber(cashflow.principalAmount) || 0), 0) * selectedUnits
    const faceValueForTrade = toNumber(listing.cashflow?.faceValueForTrade)
    const totalInterest = listing.cashflow
        ? totalCashflowInterest
        : toNumber(listing.totalInterestCashflows)
    const totalReceivable = listing.cashflow
        ? selectedUnits * (faceValueForTrade || 0) + (totalInterest || 0)
        : investmentAmount != null && totalInterest != null
            ? investmentAmount + totalInterest
            : null

    return {
        pricePerUnit,
        investmentAmount,
        totalCashflowInterest,
        totalCashflowPrincipal,
        totalInterest,
        totalReceivable
    }
}

const getSubscriptionSnapshot = (listing: BondsListingDetail) => {
    const availableUnits = toNumber(listing.unitsToSell) ?? 0
    const totalUnits = toNumber(listing.unitsAllocated) ?? 0
    const soldPercent = totalUnits > 0
        ? Math.max(0, Math.min(100, Math.round(((totalUnits - availableUnits) / totalUnits) * 100)))
        : 0

    return { availableUnits, soldPercent }
}

const getLegacyIssuerSummary = (
    listing: BondsListingDetail,
    options?: BondV2MapperOptions
) => {
    if (!options?.useLegacyIssuerAboutFallback) return ''

    const instrument = listing.instrument
    return (
        (listing.issuerFact as { aboutIssuer?: string } | undefined)?.aboutIssuer?.trim()
        || (listing as BondsListingDetail & { issuerAbout?: string }).issuerAbout?.trim()
        || (listing as BondsListingDetail & { detailShort?: string }).detailShort?.trim()
        || (instrument as typeof instrument & { detailShort?: string })?.detailShort?.trim()
        || ''
    )
}

const getIssuerDetails = (
    listing: BondsListingDetail,
    issuerBasicDetails: IssuerBasicDetails,
    options?: BondV2MapperOptions
) => {
    const instrument = listing.instrument
    const issuanceDate = issuerBasicDetails.issuanceDate?.value
        ? formatDate(issuerBasicDetails.issuanceDate.value)
        : ''
    const debentureTrustee = instrument?.debentureTrusteeName || issuerBasicDetails.debentureTrustee?.value || ''
    const displayIssuanceDate = instrument?.allotmentDate
        ? formatDate(instrument.allotmentDate)
        : issuanceDate
    const ownership = instrument?.ownership || issuerBasicDetails.ownership?.value || ''
    const fundamentalsSecurity = displayFundamentalsSecurity(listing) || issuerBasicDetails.securityType?.value || ''
    const modeOfIssuance = instrument?.modeOfIssue || issuerBasicDetails.modeOfIssuance?.value || ''

    return {
        issuerSummary: issuerBasicDetails.aboutIssuer?.value || getLegacyIssuerSummary(listing, options),
        debentureTrustee,
        displayIssuanceDate,
        ownership,
        fundamentalsSecurity,
        modeOfIssuance,
        shouldHideDebentureTrustee: !debentureTrustee,
        shouldHideIssuanceDate: !displayIssuanceDate,
        shouldHideOwnership: !ownership,
        shouldHideSecurity: !fundamentalsSecurity,
        shouldHideModeOfIssuance: !modeOfIssuance,
        debentureTrusteeLabel: instrument?.debentureTrusteeName
            ? 'Debenture trustee'
            : issuerBasicDetails.debentureTrustee?.label || 'Debenture trustee',
        issuanceDateLabel: instrument?.allotmentDate
            ? 'Issuance date'
            : issuerBasicDetails.issuanceDate?.label || 'Issuance date',
        ownershipLabel: instrument?.ownership
            ? 'Ownership'
            : issuerBasicDetails.ownership?.label || 'Ownership',
        securityLabel: displayFundamentalsSecurity(listing)
            ? 'Security'
            : issuerBasicDetails.securityType?.label || 'Security',
        modeOfIssuanceLabel: instrument?.modeOfIssue
            ? 'Mode of issuance'
            : issuerBasicDetails.modeOfIssuance?.label || 'Mode of issuance',
        whyThisBond: issuerBasicDetails.whyThisBond?.items.length
            ? issuerBasicDetails.whyThisBond.items
            : []
    }
}

type IssuerDetails = ReturnType<typeof getIssuerDetails>

const getMetrics = (
    listing: BondsListingDetail,
    pricePerUnit: number | null
) => {
    const instrument = listing.instrument
    const couponValue = getPositiveAmount(instrument?.couponRate)
    const formattedPricePerUnit = pricePerUnit == null ? null : formatAmount(pricePerUnit)

    return [
        { ...bondV2Data.metrics[0], value: displayValue(formatPercent(listing.listedYield)) },
        { ...bondV2Data.metrics[1], value: displayValue(formattedPricePerUnit) },
        { ...bondV2Data.metrics[2], value: displayValue(formatPayout(instrument?.couponFrequency ?? instrument?.interestPaymentFrequency)) },
        { ...bondV2Data.metrics[3], value: displayValue(formatDate(instrument?.maturityDate)) },
        { ...bondV2Data.metrics[4], value: displayValue(formatPositivePercent(couponValue)) },
        { ...bondV2Data.metrics[5], value: displayValue(formatAmount(instrument?.faceValue)) }
    ]
}

const getYieldHighlights = (listing: BondsListingDetail) => {
    const couponValue = getPositiveAmount(listing.instrument?.couponRate)

    return [
        {
            ...bondV2Data.yieldHighlights[0],
            value: displayValue(formatYtmCouponSpread(listing.listedYield, couponValue))
        },
        {
            ...bondV2Data.yieldHighlights[1],
            value: formatPricePair(listing)
        }
    ]
}

const getIssuerFacts = (details: IssuerDetails) => compact([
    !details.shouldHideDebentureTrustee && details.debentureTrustee
        ? { label: details.debentureTrusteeLabel, value: details.debentureTrustee }
        : null,
    !details.shouldHideIssuanceDate && details.displayIssuanceDate
        ? { label: details.issuanceDateLabel, value: details.displayIssuanceDate }
        : null
])

const mapFundamentalRow = (
    row: FundamentalRow,
    issuerName: string,
    details: IssuerDetails
) => {
    const rowHandlers: Record<string, () => FundamentalRow | null> = {
        'Issuer name': () => ({ ...row, value: issuerName }),
        'Debenture trustee': () => details.shouldHideDebentureTrustee
            ? null
            : { ...row, label: details.debentureTrusteeLabel, value: displayValue(details.debentureTrustee) },
        Ownership: () => details.shouldHideOwnership
            ? null
            : { ...row, label: details.ownershipLabel, value: details.ownership },
        Security: () => details.shouldHideSecurity
            ? null
            : { ...row, label: details.securityLabel, value: details.fundamentalsSecurity },
        'Mode of issuance': () => details.shouldHideModeOfIssuance
            ? null
            : { ...row, label: details.modeOfIssuanceLabel, value: details.modeOfIssuance },
        'Issuance date': () => details.shouldHideIssuanceDate
            ? null
            : { ...row, label: details.issuanceDateLabel, value: displayValue(details.displayIssuanceDate) }
    }

    return rowHandlers[row.label]?.() ?? row
}

const getFundamentalRows = (
    issuerName: string,
    issuerBasicDetails: IssuerBasicDetails,
    details: IssuerDetails
) => [
    ...compact(bondV2Data.fundamentals.rows.map((row) => mapFundamentalRow(row, issuerName, details))),
    ...issuerBasicDetails.extraRows
]

const getInvestTotals = (
    pricePerUnit: number | null,
    investmentAmount: number | null,
    totalInterest: number | null,
    totalReceivable: number | null
) => [
    { label: 'Price per Unit', value: displayValue(formatAmount(pricePerUnit)) },
    { label: 'Investment amount', value: displayValue(formatAmount(investmentAmount)) },
    { label: 'Total Interest', value: displayValue(formatAmount(totalInterest)) },
    {
        label: 'Total Receivable',
        value: displayValue(formatAmount(totalReceivable)),
        positive: true
    }
]

const getCashflowSummary = (
    investmentAmount: number | null,
    totalInterest: number | null,
    totalReceivable: number | null
) => [
    { value: displayValue(formatAmount(investmentAmount)), label: 'Principal Invested' },
    { value: displayValue(formatAmount(totalInterest)), label: 'Interest earned' },
    {
        value: displayValue(formatAmount(totalReceivable)),
        label: 'Final Payout',
        highlight: true
    }
]

export const mapBondListingToBondV2Data = (
    listing: BondsListingDetail | null,
    tradeDate?: string,
    units?: number,
    issuerFactsData?: BondsDetailsIssuerFactsData,
    options?: BondV2MapperOptions
): BondV2Data => {
    if (!listing) return bondV2Data

    const instrument = listing.instrument
    const issuerName = instrument?.issuerName || instrument?.name || 'N/A'
    const rating = displayRating(instrument?.creditRating)
    const ratingLabel = formatRatingLabel(rating, instrument?.creditRatingAgency)
    const instrumentType = displayInstrumentType(listing)
    const security = displaySecurity(instrument?.seniorityRepayment, instrument?.issuanceType)
    const selectedUnits = toNumber(units) ?? getSelectedUnits(listing)
    const investment = getInvestmentSnapshot(listing, selectedUnits)
    const subscription = getSubscriptionSnapshot(listing)
    const issuerBasicDetails = mapIssuerFactBasicDetails(issuerFactsData)
    const issuerDetails = getIssuerDetails(listing, issuerBasicDetails, options)
    const issuerGlanceMetrics = mapIssuerFactIssuerMetrics(issuerFactsData)

    return {
        ...bondV2Data,
        issuerName,
        isin: instrument?.isin || 'N/A',
        issuerLogo: instrument?.logoUrl || '',
        tags: unique([
            ...compact([
                ratingLabel,
                instrumentType,
                security
            ]),
            ...(instrument?.tags ?? [])
        ]),
        metrics: getMetrics(listing, investment.pricePerUnit),
        subscription: {
            ...bondV2Data.subscription,
            availableUnits: `${subscription.availableUnits} Units available`,
            soldLabel: `${subscription.soldPercent}% Sold`,
            percent: subscription.soldPercent
        },
        yieldHighlights: getYieldHighlights(listing),
        issuerSummary: issuerDetails.issuerSummary,
        issuerSummaryTitle: issuerBasicDetails.aboutIssuer?.label || bondV2Data.issuerSummaryTitle,
        issuerFacts: getIssuerFacts(issuerDetails),
        whyThisBondTitle: issuerBasicDetails.whyThisBond?.label || bondV2Data.whyThisBondTitle,
        whyThisBond: issuerDetails.whyThisBond,
        issuerGlance: {
            ...bondV2Data.issuerGlance,
            title: issuerGlanceMetrics?.title || bondV2Data.issuerGlance.title,
            metrics: issuerGlanceMetrics?.metrics ?? [],
            financialTrend: mapIssuerFactFinancialTrend(issuerFactsData)
        },
        fundamentals: {
            ...bondV2Data.fundamentals,
            title: issuerBasicDetails.title || bondV2Data.fundamentals.title,
            rows: getFundamentalRows(issuerName, issuerBasicDetails, issuerDetails),
            aboutTitle: issuerBasicDetails.aboutIssuer?.label || bondV2Data.fundamentals.aboutTitle,
            about: issuerDetails.issuerSummary
        },
        invest: {
            ...bondV2Data.invest,
            units: selectedUnits,
            stepMessage: '',
            settlementDate: formatDate(tradeDate),
            totals: getInvestTotals(
                investment.pricePerUnit,
                investment.investmentAmount,
                investment.totalInterest,
                investment.totalReceivable
            )
        },
        cashflowSummary: getCashflowSummary(
            investment.investmentAmount,
            investment.totalInterest,
            investment.totalReceivable
        ),
        cashflowTabs: getCashflowTabs(listing),
        cashflows: mapCashflows(listing, selectedUnits),
        cashflowTotals: {
            interest: displayValue(formatAmount(investment.totalCashflowInterest)),
            principal: displayValue(formatAmount(investment.totalCashflowPrincipal))
        },
        strengthsRisks: mapIssuerFactStrengthsRisks(issuerFactsData),
        ratingIntelligence: mapIssuerFactCreditRatingIntelligence(issuerFactsData, rating),
        regulatoryAssurance: mapIssuerFactRegulatoryAssurance(issuerFactsData),
        creditRiskMeter: listing.creditRiskMeter || (listing as any).credit_risk_meter || listing.instrument?.creditRiskMeter || (listing.instrument as any)?.credit_risk_meter || undefined
    }
}
